import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Loader2, RefreshCw, CheckCircle2, XCircle, Activity, Server, Clock, MemoryStick } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtUptime(s) {
    if (s == null) return '—';
    const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
    return d > 0 ? `${d} j ${h} h` : h > 0 ? `${h} h ${m} min` : `${m} min`;
}

export default function MonitoringAdmin({ auth }) {
    const api = useMemo(() => axios.create({ baseURL: API, headers: auth?.headers || {} }), [auth]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try { setData((await api.get('/tech/monitoring')).data); }
        catch { toast.error('Monitoring indisponible'); }
        finally { setLoading(false); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    if (!data) return <div className="flex justify-center py-16" data-testid="monitoring-loading"><Loader2 className="animate-spin" /></div>;

    const allOk = data.checks.every(c => c.ok);

    return (
        <div className="space-y-6" data-testid="monitoring-admin">
            <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold ${allOk ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`} data-testid="monitoring-global-status">
                    <Activity size={13} /> {allOk ? 'Tous les services opérationnels' : 'Incident détecté'}
                </span>
                <span className="text-[11px] text-foreground/50">Vérifié le {new Date(data.generated_at).toLocaleString('fr-FR')}</span>
                <Button size="sm" variant="outline" className="ml-auto" onClick={load} data-testid="monitoring-refresh">
                    <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
                </Button>
            </div>

            {/* Services */}
            <div className="grid sm:grid-cols-3 gap-3">
                {data.checks.map(c => (
                    <div key={c.name} className="rounded-xl border border-border bg-surface p-4" data-testid="monitoring-check">
                        <div className="flex items-center gap-2">
                            {c.ok ? <CheckCircle2 size={15} className="text-emerald-500" /> : <XCircle size={15} className="text-red-500" />}
                            <span className="text-sm font-medium">{c.name}</span>
                        </div>
                        <div className="font-heading text-2xl mt-2">{c.latency_ms != null ? `${c.latency_ms} ms` : '—'}</div>
                        <div className="text-[11px] text-foreground/50">latence</div>
                    </div>
                ))}
            </div>

            {/* Système */}
            <div className="grid sm:grid-cols-3 gap-3">
                <div className="rounded-xl border border-border bg-surface p-4">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-foreground/50 font-semibold"><Clock size={12} className="text-primary" /> Uptime API</div>
                    <div className="font-heading text-2xl mt-1.5" data-testid="monitoring-uptime">{fmtUptime(data.uptime_seconds)}</div>
                </div>
                <div className="rounded-xl border border-border bg-surface p-4">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-foreground/50 font-semibold"><MemoryStick size={12} className="text-primary" /> Mémoire backend</div>
                    <div className="font-heading text-2xl mt-1.5">{data.memory_mb != null ? `${data.memory_mb} Mo` : '—'}</div>
                </div>
                <div className="rounded-xl border border-border bg-surface p-4">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-foreground/50 font-semibold"><Server size={12} className="text-primary" /> Environnement</div>
                    <div className="text-sm mt-1.5 font-medium truncate">{data.environment.app_public_url || '—'}</div>
                    <div className="text-[11px] text-foreground/50">Supabase : {data.environment.supabase_project} · SMTP : {data.smtp_configured ? 'actif' : 'inactif'}</div>
                </div>
            </div>

            {/* Volumétrie */}
            <div className="rounded-xl border border-border bg-surface p-5" data-testid="monitoring-counts">
                <h3 className="font-heading text-lg mb-3">Volumétrie</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {Object.entries(data.counts).map(([label, v]) => (
                        <div key={label} className="rounded-lg border border-border p-3 text-center">
                            <div className="font-heading text-2xl">{v ?? '—'}</div>
                            <div className="text-[11px] text-foreground/50 leading-tight">{label}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
